import { useEffect, useState } from "react";
import { Badge, Button, LoadingCard, showToast } from "@xpense/ui";
import { api, AgentJob } from "../api/client";
import { EmptyState, MetricCard, Page, RetryButton, Section } from "../components/Page";
import { Icon } from "../components/Icons";

const statusToBadgeVariant: Record<string, "success" | "warning" | "error" | "info"> = {
  queued: "info",
  processing: "warning",
  completed: "success",
  failed: "error",
};

export default function AgentStatus() {
  const [jobs, setJobs] = useState<AgentJob[]>([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, processing: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<any>("/api/v1/agents/status");
      setJobs(response.jobs);
      setStats({
        total: response.total,
        pending: response.pending,
        processing: response.processing,
        failed: response.failed,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load agent status");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (jobId: string) => {
    try {
      await api.post(`/api/v1/agents/${jobId}/retry`);
      showToast("Job queued for retry", "success");
      loadJobs();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to retry job", "error");
    }
  };

  if (loading) {
    return (
      <Page
        eyebrow="Operations"
        title="Agent Status"
        description="Queue health, retries, and recent agent work across the AI pipeline."
      >
        <div className="xpn-grid xpn-grid--4">
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
        </div>
        <LoadingCard />
      </Page>
    );
  }

  if (error) {
    return (
      <Page eyebrow="Operations" title="Agent Status">
        <Section>
          <EmptyState
            icon="warning"
            title="Unable to load agent status"
            description={error}
            action={<RetryButton onClick={loadJobs} />}
            tone="danger"
          />
        </Section>
      </Page>
    );
  }

  return (
    <Page
      eyebrow="Operations"
      title="Agent Status"
      description="Watch classification, projection, search, and enrichment jobs move through the system."
      action={
        <Button variant="secondary" size="sm" onClick={loadJobs}>
          <Icon name="refresh" />
          Refresh
        </Button>
      }
    >
      <div className="xpn-grid xpn-grid--4">
        <MetricCard label="Total jobs" value={stats.total} caption="All tracked jobs" />
        <MetricCard label="Pending" value={stats.pending} caption="Queued work" tone="info" />
        <MetricCard label="Processing" value={stats.processing} caption="Currently active" tone="warning" />
        <MetricCard label="Failed" value={stats.failed} caption="Needs retry" tone={stats.failed > 0 ? "danger" : "success"} />
      </div>

      <Section title="Recent jobs" description="Latest queue work with status and retry controls.">
        {jobs.length === 0 ? (
          <EmptyState
            icon="agents"
            title="No agent jobs found"
            description="Jobs will appear here as transactions are processed."
          />
        ) : (
          <div className="xpn-row-list">
            {jobs.map((job) => (
              <div className="xpn-txn-row" key={job.id}>
                <div className="xpn-txn-main">
                  <div className="xpn-txn-icon">
                    <Icon name="agents" />
                  </div>
                  <div>
                    <div className="xpn-txn-title">{job.agent_type}</div>
                    <div className="xpn-txn-meta">
                      {new Date(job.created_at).toLocaleString("en-IN")}
                      {" · "}
                      Attempts: {job.attempts}
                    </div>
                    {job.last_error && (
                      <div className="xpn-text-caption xpn-text-danger xpn-mt-sm">
                        {job.last_error}
                      </div>
                    )}
                  </div>
                </div>
                <div className="xpn-txn-amount">
                  <Badge variant={statusToBadgeVariant[job.status] || "info"}>
                    {job.status}
                  </Badge>
                  {job.status === "failed" && (
                    <div className="xpn-mt-sm">
                      <Button variant="secondary" size="sm" onClick={() => handleRetry(job.id)}>
                        <Icon name="refresh" />
                        Retry
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </Page>
  );
}
